import React from 'react';
import { Select, Typography } from 'antd';
import { VideoCameraOutlined, StopOutlined } from '@ant-design/icons';
import type { MediaFile } from './types';

const { Option } = Select;
const { Text } = Typography;

interface VideoSelectProps {
  value?: string;
  onChange?: (value: string) => void;
  videoFiles: MediaFile[];
  loading?: boolean;
  placeholder?: string;
  allowNoVideo?: boolean;
  companyId?: number;
  companyCode?: string;
}

const VideoSelect: React.FC<VideoSelectProps> = ({
  value,
  onChange,
  videoFiles,
  loading = false,
  placeholder = '비디오 파일 선택',
  allowNoVideo = true,
  companyId,
  companyCode
}) => {
  const handleChange = (selectedValue: string) => {
    // 비어있거나 '영상 없음' 선택의 경우는 그대로 전달
    if (!selectedValue || selectedValue === '') {
      onChange?.(selectedValue);
      return;
    }

    // 선택된 비디오 파일 찾기
    const selectedFile = videoFiles.find(f => f.filename === selectedValue);

    if (selectedFile && selectedFile.file_path) {
      // file_path에서 'media/' 제거하여 올바른 경로 생성
      // 예: media/company_0/xxx.mp4 → company_0/xxx.mp4
      const fullPath = selectedFile.file_path.replace(/^media\//, '');
      onChange?.(fullPath);
      console.log(`🎥 비디오 경로 생성 (file_path 기반): ${selectedValue} → ${fullPath}`);
      console.log(`   - media.company_id: ${selectedFile.company_id}`);
      console.log(`   - media.file_path: ${selectedFile.file_path}`);
    } else {
      // file_path가 없는 경우 파일명만 사용 (Fallback)
      onChange?.(selectedValue);
      console.log(`⚠️ file_path 없음, 파일명만 사용: ${selectedValue}`);
    }
  };

  return (
    <Select
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      loading={loading}
      allowClear
      showSearch
      filterOption={(input, option) =>
        (option?.children as any)?.props?.children?.some((child: any) =>
          typeof child === 'string' && child.toLowerCase().includes(input.toLowerCase())
        )
      }
      style={{ width: '100%' }}
    >
      {allowNoVideo && (
        <Option value="" key="no-video">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <StopOutlined style={{ color: '#ff4d4f' }} />
            <span>영상 없음</span>
          </div>
        </Option>
      )}

      {videoFiles.map(file => {
        // 현재 선택된 값이 company 경로를 포함하는지 확인
        const fullPath = companyId !== undefined && companyId !== null ? `company_${companyId}/${file.filename}` : file.filename;
        const isSelected = value === fullPath || value === file.filename;

        return (
          <Option key={file.id} value={file.filename}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <VideoCameraOutlined style={{ color: '#1890ff' }} />
              <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span>{file.title || file.filename}</span>
                  {companyId !== undefined && companyId !== null && (
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                      저장 경로: company_{companyId}/{file.filename}
                      {companyId === 0 && <span style={{ color: '#52c41a', fontWeight: 'bold' }}> (시스템 공용)</span>}
                    </div>
                  )}
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  ({(file.file_size / 1024 / 1024).toFixed(1)}MB)
                  {file.duration && ` · ${Math.floor(file.duration)}초`}
                </Text>
              </div>
            </div>
          </Option>
        );
      })}
    </Select>
  );
};

export default VideoSelect;